import { RacemapColors } from '@racemap/utilities/consts/common';
import { isAccessible } from '@racemap/utilities/functions/trackers';
import type { TrackerObject } from '@racemap/utilities/types/trackers';
import { Flex } from 'antd';
import moment from 'moment';
import { type ChangeEvent, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  Column,
  type ColumnProps,
  SortDirection,
  type TableCellDataGetterParams,
  type TableCellProps,
  type TableCellRenderer,
  type TableHeaderProps,
} from 'react-virtualized';
import { TrackerColumnKeys } from 'src/store/trackers/trackers_reducers';
import { getter, keySplitter } from '../../lib/FormUtils';
import { useCurrentUser, useOutsideEvent } from '../../lib/customHooks';
import {
  Checkbox,
  type CheckboxValues,
  PopoverHint,
  Timestamp,
  TooltipWithUTCDatetime,
} from '../BasicComponents';
import { IconFilter, IconSort, IconSortDown, IconSortUp } from '../Icon';
import { HEADER_HEIGHT, defaultCellStyle } from './DataTable';
import type { CustomSortingMethod } from './helpers';

export type BasicColumnProps<T = unknown, C = unknown> = Omit<
  ColumnProps,
  'width' | 'columnData'
> & {
  width?: number;
  hasRightBorder?: boolean;
  sortingMethod?: CustomSortingMethod<T, C>;
  headerTitle?: string;
  hint?: React.ReactNode;
  hintWidth?: number;
  columnData?: {
    searchable?: boolean;
    selection?: Array<string>;
    hiddenOnDefault?: boolean;
    cellDataFallback?: string;
  };
};

export const basicColumn = <Row = unknown, Cell = unknown>({
  style,
  hasRightBorder,
  headerStyle,
  columnData: {
    searchable = true,
    selection,
    hiddenOnDefault = false,
    cellDataFallback,
    ...customColumnData
  } = {},
  ...props
}: BasicColumnProps<Row, Cell>) => {
  const basicProps = {
    width: 0,
    disableSort: true,
    columnData: {
      ...customColumnData,
      searchable,
      selection,
      hiddenOnDefault,
      cellDataFallback,
    },
  };

  const styleWithAdditions = {
    ...defaultCellStyle,
    ...style,
  };
  const headerStyleWithAdditions = {
    ...defaultCellStyle,
    ...(headerStyle || style),
  };

  if (hasRightBorder) {
    styleWithAdditions.borderRight = 'solid 1px #dee2e6';
    headerStyleWithAdditions.borderRight = 'solid 1px #dee2e6';
  }

  function headerRenderer(headerProps: TableHeaderProps) {
    const content = props.headerRenderer?.(headerProps) || headerProps.label;
    const title = props.headerTitle || headerProps.label?.toString() || '';
    const hint = props.hint ? (
      <PopoverHint width={props.hintWidth}>{props.hint}</PopoverHint>
    ) : null;

    if (props.headerRenderer == null)
      return (
        <Flex justify="space-between" align="center">
          <div title={title}>{content}</div>
          {hint}
        </Flex>
      );
    return content;
  }

  function cellDataGetter(getterParams: TableCellDataGetterParams) {
    const { dataKey, rowData } = getterParams;
    const path = keySplitter(dataKey);
    const data = getter(path, rowData);

    return data !== undefined ? data : cellDataFallback;
  }

  function defaultCellRenderer(cellProps: TableCellProps) {
    if (typeof cellProps.cellData === 'string') {
      return (
        <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          {cellProps.cellData}
        </div>
      );
    }

    return cellProps.cellData;
  }

  return (
    <Column
      {...basicProps}
      style={styleWithAdditions}
      {...props}
      headerStyle={headerStyleWithAdditions}
      headerRenderer={headerRenderer}
      cellDataGetter={cellDataGetter}
      cellRenderer={props.cellRenderer || defaultCellRenderer}
    />
  );
};

export const sortingColumn = <Row = unknown, Cell = unknown>(
  props: BasicColumnProps<Row, Cell>,
) => {
  function headerRenderer({ dataKey, label, sortBy, sortDirection }: TableHeaderProps) {
    const iconStyle = {
      color: dataKey !== sortBy ? RacemapColors.Gray : RacemapColors.PaleBlue,
      fontSize: '0.8em',
      paddingLeft: 5,
    };

    return (
      <span
        className="ReactVirtualized__Table__headerTruncatedText"
        key="label"
        title={props.headerTitle || label?.toString() || ''}
      >
        <Flex justify="space-between" align="center">
          {label}
          {props.hint && <PopoverHint width={props.hintWidth}>{props.hint}</PopoverHint>}
          {sortBy !== dataKey && <IconSort style={iconStyle} />}
          {sortBy === dataKey && sortDirection === SortDirection.ASC && (
            <IconSortUp style={iconStyle} />
          )}
          {sortBy === dataKey && sortDirection === SortDirection.DESC && (
            <IconSortDown style={iconStyle} />
          )}
        </Flex>
      </span>
    );
  }

  return basicColumn({ headerRenderer, disableSort: false, ...props });
};

interface FilterColumnProps extends BasicColumnProps {
  options: Array<string>;
  optionsLabel?: Record<string, string>;
  onNewSelection?: (newSelection: Array<string>) => void;
}

export const filterColumn = (props: FilterColumnProps) => {
  const { options, optionsLabel, ...basicProps } = props;
  const [selection, setSelection] = useState<Array<string>>([]);
  const [filterChooserVisible, setFilterChooserVisible] = useState(false);
  const tooltipRef = useRef(null);
  const labelRef = useRef(null);
  useOutsideEvent(tooltipRef, () => setFilterChooserVisible(false), [labelRef]);
  const isActive = selection.length > 0;

  function handleCheckboxChange(_: ChangeEvent<HTMLInputElement>, checkboxValue: string) {
    const newSelection = new Set(selection);

    if (newSelection.has(checkboxValue)) {
      newSelection.delete(checkboxValue);
    } else {
      newSelection.add(checkboxValue);
    }
    const selectedItems = Array.from(newSelection);
    props.onNewSelection?.(selectedItems);
    setSelection(selectedItems);
  }

  function handleFilterButtonClick() {
    setFilterChooserVisible(!filterChooserVisible);
  }

  function headerRenderer({ label }: TableHeaderProps) {
    return (
      <div style={{ height: '100%' }}>
        <span
          className="ReactVirtualized__Table__headerTruncatedText"
          key="label"
          title={props.headerTitle || label?.toString() || ''}
          style={{
            cursor: 'pointer',
            display: 'flex',
            height: '100%',
            alignItems: 'center',
          }}
          onClick={handleFilterButtonClick}
          onKeyDown={handleFilterButtonClick}
          ref={labelRef}
        >
          <>
            {label}
            <IconFilter
              style={{
                color: isActive ? RacemapColors.PaleBlue : RacemapColors.Gray,
                fontSize: '0.8em',
                paddingLeft: 5,
              }}
            />
          </>
        </span>
        {filterChooserVisible && (
          <div
            style={{
              position: 'absolute',
              border: 'solid 1px #dee2e6',
              borderRadius: '0.25rem',
              background: 'white',
              zIndex: 10,
              top: HEADER_HEIGHT + 3,
              padding: 10,
            }}
            ref={tooltipRef}
          >
            {options.map((o) => (
              <Form.Check
                key={o}
                type="checkbox"
                label={optionsLabel != null ? optionsLabel[o] || o : o}
                checked={selection.includes(o)}
                onChange={(event) => handleCheckboxChange(event, o)}
                style={{
                  margin: 3,
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return basicColumn({
    headerRenderer,
    disableSort: true,
    ...basicProps,
    columnData: props.onNewSelection
      ? basicProps.columnData
      : { ...basicProps.columnData, selection },
  });
};

export const fromNowColumn = (props: BasicColumnProps<unknown, Date>) => {
  const currentUser = useCurrentUser();

  const fromNowCellRenderer: TableCellRenderer = ({
    cellData,
    rowData,
  }: {
    cellData?: Date | number | string;
    rowData: TrackerObject;
  }) => {
    if (cellData == null || !isAccessible(rowData, currentUser)) return <></>;
    return <Timestamp time={new Date(cellData)} />;
  };

  return sortingColumn({
    cellRenderer: fromNowCellRenderer,
    sortingMethod: (cellValueA: Date, cellValueB: Date) =>
      dateColumnSortMethod(cellValueA, cellValueB),
    ...props,
  });
};

export const dateColumnSortMethod = (
  a?: Date | number | string,
  b?: Date | number | string,
): number => {
  if (a == null) return -1;
  if (b == null) return 1;

  return new Date(b).valueOf() - new Date(a).valueOf();
};

export const timeColumn = (props: BasicColumnProps<unknown, Date>) => {
  const fromNowCellRenderer: TableCellRenderer = ({ cellData }: { cellData?: Date }) => {
    if (cellData == null || !moment(cellData).isValid()) return <></>;
    return (
      <TooltipWithUTCDatetime datetime={cellData}>
        <div>{moment(cellData).format('LTS')}</div>
      </TooltipWithUTCDatetime>
    );
  };

  return sortingColumn({
    cellRenderer: fromNowCellRenderer,
    sortingMethod: dateColumnSortMethod,
    ...props,
  });
};

interface LinkColumnProps<T> extends BasicColumnProps {
  linkGetter: (cellData: unknown, rowData: T) => string | null;
  icon: React.ReactElement;
  options?: Array<string>;
}

export function linkColumn<T>(props: LinkColumnProps<T>) {
  const cellRenderer: TableCellRenderer = ({
    cellData,
    rowData,
  }: {
    cellData?: string;
    rowData: T;
  }) => {
    const linkUrl = props.linkGetter(cellData, rowData);

    if (linkUrl == null) return <></>;
    return <Link to={linkUrl}>{props.icon}</Link>;
  };

  if (props.options != null) {
    return filterColumn({
      cellRenderer,
      options: props.options,
      minWidth: 30,
      width: 30,
      ...props,
    });
  }
  return basicColumn({ cellRenderer, minWidth: 30, width: 30, ...props });
}

export function rowIndexColumn(props: Omit<BasicColumnProps, 'dataKey'> = {}) {
  const cellRenderer: TableCellRenderer = ({ rowIndex }: { rowIndex: number }) => {
    return (
      <>
        <b>{rowIndex + 1}</b>
      </>
    );
  };

  return basicColumn({
    cellRenderer,
    minWidth: 50,
    width: 50,
    dataKey: TrackerColumnKeys.INDEX,
    label: '#',
    ...props,
    columnData: { ...props.columnData, searchable: false },
  });
}

type CheckboxColumnProps = Omit<BasicColumnProps, 'dataKey'> & {
  onRowSelectionChange: ({ index, newValue }: { index: number; newValue: boolean }) => void;
  getRowSelectionState: (options: { index: number }) => CheckboxValues;
  isSortingCheckboxCol: boolean;
};

export function checkboxColumn({
  onRowSelectionChange,
  getRowSelectionState,
  isSortingCheckboxCol,
  ...props
}: CheckboxColumnProps) {
  const label = (
    <Checkbox
      value={getRowSelectionState({ index: -1 })}
      onChange={(newValue) => onRowSelectionChange({ index: -1, newValue })}
    />
  );
  const cellRenderer: TableCellRenderer = ({ rowIndex }) => {
    if (rowIndex == null) return <></>;
    const isSelected = getRowSelectionState({ index: rowIndex });

    return (
      <Checkbox
        value={isSelected}
        onChange={(newValue) => onRowSelectionChange({ index: rowIndex, newValue })}
      />
    );
  };

  const columnProps = {
    cellRenderer,
    minWidth: isSortingCheckboxCol ? 40 : 30,
    width: isSortingCheckboxCol ? 40 : 30,
    dataKey: '_selection_',
    label,
    ...props,
    columnData: { ...props.columnData, searchable: false },
  };

  return isSortingCheckboxCol
    ? sortingColumn({ ...columnProps, headerStyle: { paddingLeft: '10px' } })
    : basicColumn(columnProps);
}

export function placeholderColumn() {
  return basicColumn({
    cellRenderer: () => null,
    style: { display: 'none' },
    headerStyle: { marginLeft: 'auto' },
    minWidth: 30,
    width: 30,
    dataKey: '',
  });
}
